import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronDown, Loader2, Plus, Search, X } from 'lucide-react'
import clsx from 'clsx'

import { api } from '@/lib/api'
import { normalizar } from '@/lib/formato'

export interface ItemLookup {
  id: number
  code: string
  nombre: string
  detalle?: string | null
  color?: string | null
  icono?: string | null
  meta?: Record<string, unknown> | null
}

interface Props {
  /**
   * Ruta del lookup, sin el prefijo: 'unidades', 'catalogo/MOTIVO_MERMA'.
   *
   * Se omite cuando la lista es LOCAL y viene en `items`.
   */
  fuente?: string

  /**
   * Lista local, para cuando las opciones no salen de un endpoint.
   *
   * El caso real es el mapeo de columnas de una planilla: las opciones son las
   * columnas del archivo que se acaba de abrir, y no hay ningún lookup que las
   * sirva. Sin esto ahí habría un `<select>` pelado, que es justo lo que este
   * componente existe para que no haya.
   */
  items?: ItemLookup[]

  valor: number | null
  onCambio: (id: number | null, item: ItemLookup | null) => void

  etiqueta?: string
  ayuda?: string
  error?: string
  placeholder?: string
  requerido?: boolean
  deshabilitado?: boolean

  /** Permite crear una opción al vuelo, sin abandonar el formulario. */
  onCrear?: (texto: string) => void
  textoCrear?: string

  /** Para poder limpiar la selección. */
  permiteVaciar?: boolean

  claseContenedor?: string
}

// EL combo del sistema. Es el ÚNICO que se usa: nunca un select pelado.
//
// Por qué: en cuanto una lista pasa de ocho opciones, un desplegable nativo
// obliga a scrollear buscando con la vista. Acá se tipea y filtra, que es lo
// que la gente hace por instinto.
//
// Filtra en dos niveles: el servidor devuelve los primeros N que matchean (y
// así funciona con listas grandes), y el cliente refina sobre lo ya traído sin
// esperar el ida y vuelta, tolerando acentos porque nadie los tipea.
export function LookupSelect({
  fuente,
  items: locales,
  valor,
  onCambio,
  etiqueta,
  ayuda,
  error,
  placeholder = 'Buscar…',
  requerido,
  deshabilitado,
  onCrear,
  textoCrear = 'Crear',
  permiteVaciar,
  claseContenedor,
}: Props) {
  const [abierto, setAbierto] = useState(false)
  const [texto, setTexto] = useState('')
  const [resaltado, setResaltado] = useState(0)

  const idCampo = useId()
  const contenedor = useRef<HTMLDivElement>(null)

  // La lista se monta FUERA del contenedor, y hay que resolver dos cosas.
  //
  // Una: por qué. El combo aparece dentro de un `Modal`, cuyo cuerpo scrollea,
  // y también dentro del cuerpo de una grilla; como `absolute` hijo del campo,
  // la lista quedaba recortada por esa caja —se veía media opción y las de
  // abajo no se podían ni tocar— y ningún z-index lo arregla, porque el
  // recorte no es un problema de capas.
  //
  // Dos: DÓNDE. Al revés que el menú de acciones de una fila, acá el `body` no
  // sirve: un `<dialog>` modal vive en la capa superior del navegador, así que
  // cualquier nodo colgado del body queda POR DEBAJO del diálogo, invisible y
  // sin poder recibir clics. Se busca el `<dialog>` que contiene al campo y se
  // monta ahí; si no hay ninguno —el combo suelto de una pantalla— va al body.
  // El nodo donde se monta va en el ESTADO y no se resuelve al renderizar:
  // leerlo de un ref durante el render es justamente lo que el linter frena, y
  // con razón —React no re-renderiza cuando un ref cambia—. Se decide una vez,
  // al abrir la lista.
  const [caja, setCaja] = useState<{
    /** Uno de los dos, según de qué lado se abre. Ver `medir`. */
    top?: number
    bottom?: number
    left: number
    width: number
    alto: number
    nodo: Element
  } | null>(null)

  const lista = useRef<HTMLDivElement>(null)

  const inputBusqueda = useRef<HTMLInputElement>(null)

  // `incluirId` hace que el ítem seleccionado venga en la respuesta incluso si
  // está inactivo o no entra en los primeros N: si no, el combo aparecería
  // vacío al editar un registro viejo.
  const { data: traidos = [], isFetching } = useQuery({
    queryKey: ['lookup', fuente, texto, valor],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '50' })
      if (texto.trim()) params.set('q', texto.trim())
      if (valor) params.set('incluirId', String(valor))

      return api.get<ItemLookup[]>(`/lookups/${fuente!}?${params}`)
    },
    // Con lista local no hay nada que pedir.
    enabled: locales === undefined && fuente !== undefined,

    // Se mantiene lo anterior mientras llega lo nuevo: sin esto la lista
    // parpadea en vacío con cada tecla.
    placeholderData: (previo) => previo,
    staleTime: 30_000,
  })

  const items = locales ?? traidos

  const seleccionado = useMemo(() => items.find((i) => i.id === valor) ?? null, [items, valor])

  // Refinado local sobre lo ya traído, sin acentos.
  const filtrados = useMemo(() => {
    if (!texto.trim()) return items
    const q = normalizar(texto)

    return items.filter(
      (i) =>
        normalizar(i.nombre).includes(q) ||
        normalizar(i.code).includes(q) ||
        (i.detalle ? normalizar(i.detalle).includes(q) : false),
    )
  }, [items, texto])

  // Cerrar la lista limpia lo tipeado: si no, al volver a abrir el combo
  // aparece filtrado por una búsqueda vieja y parece que faltan opciones.
  // Está en una función porque hay seis lugares que cierran la lista (elegir,
  // Escape, Tab, clic afuera, crear al vuelo) y en cada uno hay que hacer las
  // dos cosas.
  const cerrarLista = () => {
    setAbierto(false)
    setTexto('')
    setCaja(null)
  }

  // La posición, medida contra el nodo donde se monta la lista.
  //
  // Se posiciona `fixed`, pero `fixed` NO siempre es relativo al viewport, y
  // esto lo dijo el navegador: el `Modal` del sistema tiene un `transform`, y
  // un elemento con transform es el bloque contenedor de sus descendientes
  // fijos. O sea que adentro de un diálogo las coordenadas son las DEL
  // DIÁLOGO. Medido antes de restar el origen: la lista aparecía 391 px a la
  // derecha del campo, que es exactamente donde arranca el diálogo.
  //
  // Se resta el PADDING box y no el borde exterior (`clientLeft`/`clientTop`
  // son justamente los anchos de borde), porque el bloque contenedor de un
  // posicionado es el padding box. Contra el `body` el origen es 0,0 y la
  // cuenta no cambia nada.
  const medir = () => {
    const campo = contenedor.current?.querySelector('button')?.getBoundingClientRect()
    if (!campo) return

    const nodo = contenedor.current?.closest('dialog') ?? document.body
    const base = nodo === document.body ? null : nodo.getBoundingClientRect()

    const origenX = base ? base.left + nodo.clientLeft : 0
    const origenY = base ? base.top + nodo.clientTop : 0

    // Y se decide de qué LADO abrir y con qué alto, porque salirse del nodo
    // que la contiene la recorta igual: adentro de un diálogo no alcanza con
    // escapar del cuerpo scrolleable, la tarjeta también corta. Es el mismo
    // criterio del `<select>` nativo, que nunca se va de la ventana.
    const limite = base
      ? { arriba: base.top + nodo.clientTop, abajo: base.top + nodo.clientTop + nodo.clientHeight }
      : { arriba: 0, abajo: window.innerHeight }

    const margen = 8
    const espacioAbajo = limite.abajo - campo.bottom - margen
    const espacioArriba = campo.top - limite.arriba - margen

    // Se abre para arriba solo si abajo no entra ni lo mínimo legible y arriba
    // hay más lugar: dar vuelta la lista por veinte píxeles marea más de lo
    // que ayuda.
    const paraArriba = espacioAbajo < 160 && espacioArriba > espacioAbajo

    const alto = Math.max(120, Math.min(340, paraArriba ? espacioArriba : espacioAbajo))

    // Abriendo para arriba se ancla por ABAJO y no por arriba, y la
    // diferencia se ve: con `top` calculado desde el alto DISPONIBLE, una
    // lista de tres opciones quedaba flotando 180 px arriba del campo, porque
    // el alto real es el de las opciones y no el del hueco. Anclada por abajo,
    // el borde inferior queda pegado al campo y la lista crece hacia arriba.
    const altoDelBloque = base ? nodo.clientHeight : window.innerHeight

    setCaja({
      ...(paraArriba
        ? { bottom: altoDelBloque - (campo.top - origenY) + 6 }
        : { top: campo.bottom - origenY + 6 }),
      left: campo.left - origenX,
      width: campo.width,
      alto,
      nodo,
    })
  }

  const alternar = () => {
    if (deshabilitado) return

    if (abierto) {
      cerrarLista()
      return
    }

    setResaltado(0)
    medir()
    setAbierto(true)
  }

  // Cerrar al hacer click afuera.
  useEffect(() => {
    if (!abierto) return

    // Los DOS nodos: la lista ya no es hija del contenedor, y mirando solo el
    // contenedor el `mousedown` sobre una opción cerraba la lista antes de que
    // llegara su `click`. Es el mismo detalle que el menú de acciones de fila.
    const alClick = (e: MouseEvent) => {
      const destinoClick = e.target as Node

      if (
        !contenedor.current?.contains(destinoClick) &&
        !lista.current?.contains(destinoClick)
      ) {
        cerrarLista()
      }
    }

    // Al scrollear la lista SE REACOMODA, no se cierra, y esto es un bug que
    // encontró el navegador y no se ve leyendo el código: al abrir, el foco va
    // al buscador, y enfocar algo dentro del cuerpo scrolleable de un diálogo
    // dispara un `scroll`. Con «cerrar» ahí, la lista se cerraba sola en el
    // mismo instante en que se abría, y solo adentro de un diálogo.
    //
    // Reacomodar es además lo correcto para un campo de formulario: la lista
    // pertenece al campo y tiene que seguirlo. (El menú de acciones de una
    // fila hace lo contrario, y también corresponde: ese no es un campo, es un
    // menú que se abrió para elegir una opción ahora.)
    document.addEventListener('mousedown', alClick)
    window.addEventListener('scroll', medir, true)
    window.addEventListener('resize', medir)

    return () => {
      document.removeEventListener('mousedown', alClick)
      window.removeEventListener('scroll', medir, true)
      window.removeEventListener('resize', medir)
    }
  }, [abierto])

  // En el efecto queda SOLO el foco, que es lo único que toca algo externo a
  // React. El reset del texto y del resaltado se hace al abrir y al cerrar,
  // donde corresponde: hacerlo en el efecto disparaba un render de más por
  // cada apertura del combo, y en una pantalla con seis combos se nota.
  useEffect(() => {
    if (abierto) inputBusqueda.current?.focus()
  }, [abierto])

  const elegir = (item: ItemLookup) => {
    onCambio(item.id, item)
    cerrarLista()
  }

  // Teclado completo: en el mostrador y en carga masiva de datos, tener que
  // pasar al mouse por cada combo cuesta más que todo lo demás junto.
  const alTeclear = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setResaltado((r) => Math.min(r + 1, filtrados.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setResaltado((r) => Math.max(r - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = filtrados[resaltado]
      if (item) elegir(item)
      else if (onCrear && texto.trim()) {
        onCrear(texto.trim())
        cerrarLista()
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cerrarLista()
    } else if (e.key === 'Tab') {
      cerrarLista()
    }
  }

  return (
    <div className={claseContenedor} ref={contenedor}>
      {etiqueta && (
        <label htmlFor={idCampo} className="etiqueta">
          {etiqueta}
          {requerido && <span className="ml-0.5 text-rojo">*</span>}
        </label>
      )}

      <div className="relative">
        <button
          id={idCampo}
          type="button"
          onClick={alternar}
          disabled={deshabilitado}
          className={clsx(
            'campo flex items-center justify-between gap-2 text-left',
            error && 'campo-error',
            deshabilitado && 'cursor-not-allowed',
          )}
          aria-haspopup="listbox"
          aria-expanded={abierto}
          aria-invalid={!!error}
        >
          <span className="flex min-w-0 items-center gap-2">
            {seleccionado?.color && (
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: seleccionado.color }}
                aria-hidden
              />
            )}
            {/* El NOMBRE es lo que dice qué está elegido, así que es el que
                tiene que sobrevivir cuando no entra todo. Estaba al revés —el
                detalle con `shrink-0`— y en un teléfono el campo mostraba solo
                el detalle: el combo de la impresora decía «Tinta, láser o
                Print to PDF» sin decir cuál de los modos era. En pantalla
                angosta el detalle no se muestra: no hay lugar para los dos y
                está en la lista al abrirla. */}
            <span className={clsx('min-w-0 truncate', !seleccionado && 'text-tinta-tenue')}>
              {seleccionado?.nombre ?? placeholder}
            </span>
            {seleccionado?.detalle && (
              <span className="num hidden min-w-0 truncate text-[12.5px] text-tinta-tenue sm:inline">
                {seleccionado.detalle}
              </span>
            )}
          </span>

          <span className="flex shrink-0 items-center gap-1">
            {permiteVaciar && seleccionado && !deshabilitado && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Quitar selección"
                onClick={(e) => {
                  e.stopPropagation()
                  onCambio(null, null)
                }}
                className="rounded p-0.5 text-tinta-tenue hover:text-tinta"
              >
                <X className="h-4 w-4" />
              </span>
            )}
            <ChevronDown
              className={clsx(
                'h-4 w-4 text-tinta-tenue transition-transform',
                abierto && 'rotate-180',
              )}
            />
          </span>
        </button>

        {abierto &&
          caja !== null &&
          createPortal(
            <div
              ref={lista}
              style={{
                top: caja.top,
                bottom: caja.bottom,
                left: caja.left,
                width: caja.width,
                maxHeight: caja.alto,
              }}
              className="fixed z-50 flex flex-col overflow-hidden rounded-lg border border-borde
                         bg-papel shadow-elevada"
            >
              <div className="flex shrink-0 items-center gap-2 border-b border-borde px-3">
                <Search className="h-4 w-4 shrink-0 text-tinta-tenue" />
                <input
                  ref={inputBusqueda}
                  value={texto}
                  onChange={(e) => {
                    setTexto(e.target.value)
                    setResaltado(0)
                  }}
                  onKeyDown={alTeclear}
                  placeholder="Escribí para filtrar…"
                  className="w-full border-0 bg-transparent py-2.5 text-[14.5px] outline-none
                             placeholder:text-tinta-tenue"
                  autoComplete="off"
                  spellCheck={false}
                />
                {isFetching && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-tinta-tenue" />}
              </div>

              {/* El alto lo reparte el flex del contenedor, que está acotado
                  a lo que hay disponible: sin esto, el buscador quedaba fijo y
                  las opciones se pasaban del borde igual. */}
              <ul role="listbox" className="min-h-0 flex-1 overflow-y-auto py-1">
                {filtrados.length === 0 && (
                  <li className="px-3 py-6 text-center text-[13.5px] text-tinta-tenue">
                    {texto.trim() ? `Sin resultados para «${texto.trim()}»` : 'No hay opciones'}
                  </li>
                )}

                {filtrados.map((item, i) => (
                  <li key={item.id} role="option" aria-selected={item.id === valor}>
                    <button
                      type="button"
                      onClick={() => elegir(item)}
                      onMouseEnter={() => setResaltado(i)}
                      className={clsx(
                        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-[14.5px]',
                        i === resaltado && 'bg-crema-alt',
                        item.id === valor && 'font-semibold',
                      )}
                    >
                      {item.color && (
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ background: item.color }}
                          aria-hidden
                        />
                      )}

                      <span className="min-w-0 flex-1 truncate">{item.nombre}</span>

                      {item.detalle && (
                        <span className="num shrink-0 text-[12.5px] text-tinta-tenue">
                          {item.detalle}
                        </span>
                      )}

                      {item.id === valor && <Check className="h-4 w-4 shrink-0 text-verde" />}
                    </button>
                  </li>
                ))}
              </ul>

              {onCrear && texto.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    onCrear(texto.trim())
                    cerrarLista()
                  }}
                  className="flex w-full items-center gap-2 border-t border-borde px-3 py-2.5
                             text-left text-[14px] font-semibold text-verde hover:bg-verde-tenue"
                >
                  <Plus className="h-4 w-4" />
                  {textoCrear} «{texto.trim()}»
                </button>
              )}
            </div>,
            caja.nodo,
          )}
      </div>

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : (
        ayuda && <p className="ayuda">{ayuda}</p>
      )}
    </div>
  )
}
